import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Bell,
  ClipboardCheck,
  Users,
  CalendarDays,
  BriefcaseBusiness,
  WalletCards,
  Package,
  ShoppingCart,
  ArrowRight,
  BookOpen,
  CheckCircle2,
  Download,
  Megaphone,
  Pin,
  Bookmark,
  BookmarkCheck,
  BellRing,
} from "lucide-react";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";

import { fetchDashboard, markAllNotificationsRead } from "../api";
import {
  useActiveNotices,
  usePinNotice,
  useUnpinNotice,
  useRequestNoticeReminder,
  useCancelNoticeReminder,
} from "#/features/notices/hooks";
import type { Notice } from "#/features/notices/types";
import { membershipApi } from "#/features/membership/api";
import { leaveApi } from "#/features/leave/api";
import { fetchMyClaims } from "#/features/claims/api";
import { trainingApi } from "#/features/training/api";
import { hrRequisitionApi } from "#/features/requisitions/api";
import { calendarApi } from "#/features/calendar/api";
import { fetchUsers } from "#/features/users/api";
import { PageLoader } from "#/components/common/PageLoader";
import { ErrorState } from "#/components/common/ErrorState";
import { Modal } from "#/components/common/Modal";
import { useAuth } from "#/context/AuthProvider";
import { useFeatureHint } from "#/features/feature-hints/hooks";
import type {
  DashboardApprovalItem,
  DashboardNotificationItem,
} from "../types";

/** Statuses that mean "this item is done" — everything else counts as still pending. */
const TERMINAL_STATUSES = new Set([
  "DRAFT", "APPROVED", "REJECTED", "CANCELLED", "PAID", "COMPLETED",
  "PURCHASED", "CLOSED", "FULFILLED", "RECALLED", "ARCHIVED", "DELETED",
]);

/** A single row shown inside a dashboard drill-down modal. */
type DrillDownItem = {
  key: string;
  module?: string;
  title: string;
  subtitle?: string | null;
  status?: string | null;
  route: string;
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmt(value?: string | null) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString("en-KE", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function n(v: unknown, fallback = 0) {
  return typeof v === "number" && Number.isFinite(v) ? v : fallback;
}

// ── Atoms ─────────────────────────────────────────────────────────────────────

function StatCard({
  label,
  value,
  hint,
  icon: Icon,
  tone = "slate",
  to,
  onClick,
}: {
  label: string;
  value: number | string;
  hint?: string;
  icon: React.ComponentType<{ className?: string }>;
  tone?: "emerald" | "blue" | "amber" | "red" | "slate" | "purple" | "teal" | "orange";
  to?: string;
  onClick?: () => void;
}) {
  const tones: Record<string, { card: string; icon: string; value: string }> = {
    emerald: { card: "border-emerald-100 bg-gradient-to-br from-emerald-50 to-white", icon: "bg-emerald-100 text-emerald-600", value: "text-emerald-700" },
    blue:    { card: "border-blue-100 bg-gradient-to-br from-blue-50 to-white",       icon: "bg-blue-100 text-blue-600",       value: "text-blue-700" },
    amber:   { card: "border-amber-100 bg-gradient-to-br from-amber-50 to-white",     icon: "bg-amber-100 text-amber-600",     value: "text-amber-700" },
    red:     { card: "border-red-100 bg-gradient-to-br from-red-50 to-white",         icon: "bg-red-100 text-red-600",         value: "text-red-700" },
    slate:   { card: "border-slate-200 bg-white",                                     icon: "bg-slate-100 text-slate-500",     value: "text-slate-700" },
    purple:  { card: "border-purple-100 bg-gradient-to-br from-purple-50 to-white",   icon: "bg-purple-100 text-purple-600",   value: "text-purple-700" },
    teal:    { card: "border-teal-100 bg-gradient-to-br from-teal-50 to-white",       icon: "bg-teal-100 text-teal-600",       value: "text-teal-700" },
    orange:  { card: "border-orange-100 bg-gradient-to-br from-orange-50 to-white",   icon: "bg-orange-100 text-orange-600",   value: "text-orange-700" },
  };

  const t = tones[tone];

  const inner = (
    <div className={`flex items-center gap-3 rounded-2xl border p-4 shadow-sm transition hover:shadow-md h-full ${t.card}`}>
      <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${t.icon}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-xs font-medium text-slate-500">{label}</p>
        <p className={`text-xl font-bold leading-tight ${t.value}`}>{value}</p>
        {hint && <p className="truncate text-[11px] text-slate-400">{hint}</p>}
      </div>
      {(to || onClick) && <ArrowRight className="h-4 w-4 shrink-0 text-slate-300" />}
    </div>
  );

  if (onClick) {
    return (
      <button type="button" onClick={onClick} className="block w-full text-left">
        {inner}
      </button>
    );
  }
  return to ? <Link to={to}>{inner}</Link> : inner;
}

function SectionCard({
  title,
  description,
  action,
  children,
  id,
}: {
  title: string;
  description?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
  id?: string;
}) {
  return (
    <section id={id} className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-5 flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold text-slate-900">{title}</h2>
          {description && <p className="mt-1 text-sm text-slate-500">{description}</p>}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

// ── Module / status colours ───────────────────────────────────────────────────

const MODULE_CLS: Record<string, string> = {
  LEAVE:       "bg-blue-100 text-blue-700",
  CLAIM:       "bg-amber-100 text-amber-700",
  TRAINING:    "bg-purple-100 text-purple-700",
  INVENTORY:   "bg-teal-100 text-teal-700",
  REQUISITION: "bg-orange-100 text-orange-700",
};

const STATUS_CLS: Record<string, string> = {
  SUBMITTED:           "bg-amber-50 text-amber-700 border border-amber-200",
  PENDING_APPROVAL:    "bg-amber-50 text-amber-700 border border-amber-200",
  APPROVED:            "bg-emerald-100 text-emerald-800",
  REJECTED:            "bg-red-100 text-red-700",
  CANCELLED:           "bg-slate-100 text-slate-600",
  QUOTATION_REQUESTED: "bg-purple-100 text-purple-700",
  QUOTATIONS_RECEIVED: "bg-indigo-100 text-indigo-700",
  BUDGET_APPROVED:     "bg-teal-100 text-teal-700",
  BUDGET_REJECTED:     "bg-red-100 text-red-700",
  EVALUATED:           "bg-orange-100 text-orange-700",
};

function ModuleBadge({ module }: { module: string }) {
  const cls = MODULE_CLS[module] ?? "bg-slate-100 text-slate-600";
  return (
    <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${cls}`}>
      {module}
    </span>
  );
}

function StatusChip({ value }: { value?: string | null }) {
  const cls = STATUS_CLS[value ?? ""] ?? "bg-slate-100 text-slate-600";
  return (
    <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${cls}`}>
      {(value ?? "").replace(/_/g, " ")}
    </span>
  );
}

// ── Approval list (compact — 3 items + see all) ───────────────────────────────

function ApprovalList({ items }: { items: DashboardApprovalItem[] }) {
  const navigate = useNavigate();
  const visible = items.slice(0, 3);
  const extra = Math.max(0, items.length - 3);

  if (!items.length) {
    return (
      <div className="flex items-center gap-3 rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-5">
        <CheckCircle2 className="h-5 w-5 flex-shrink-0 text-emerald-400" />
        <p className="text-sm text-slate-500">All caught up — no pending approvals.</p>
      </div>
    );
  }

  return (
    <div className="space-y-1.5">
      {visible.map((item) => {
        const module = item.module ?? item.type ?? "—";
        const ref = item.referenceNo ?? `#${item.id}`;
        const from = item.submittedBy ?? "";
        const route = item.route ?? "#";
        return (
          <div
            key={item.id}
            onClick={() => navigate(route)}
            className="flex cursor-pointer items-center gap-2.5 rounded-xl border border-slate-100 bg-slate-50 px-3 py-2.5 transition hover:border-emerald-200 hover:bg-emerald-50"
          >
            <ModuleBadge module={module} />
            <div className="min-w-0 flex-1">
              <p className="truncate text-xs font-semibold text-slate-800">{ref}</p>
              {from && <p className="truncate text-xs text-slate-400">{from}</p>}
            </div>
            <StatusChip value={item.status} />
            <ArrowRight className="h-3.5 w-3.5 flex-shrink-0 text-slate-400" />
          </div>
        );
      })}
      {extra > 0 && (
        <Link
          to="/approvals"
          className="block pt-1 text-center text-xs font-semibold text-emerald-700 hover:underline"
        >
          + {extra} more — view all approvals
        </Link>
      )}
    </div>
  );
}

// ── Notification list ─────────────────────────────────────────────────────────

function notifRoute(item: DashboardNotificationItem): string | null {
  // Routes that need a referenceId suffix
  if (item.referenceId) {
    const withId: Record<string, string> = {
      // Leave
      LEAVE:            "/hr/leave/requests",
      LEAVE_REQUEST:    "/hr/leave/requests",
      // Claims  (router: /claims/:id)
      CLAIM:            "/claims",
      // Training  (router: /hr/training/:id)
      TRAINING:         "/hr/training",
      TRAINING_REQUEST: "/hr/training",
      // HR Requisitions  (router: /hr/requisitions/:id)
      REQUISITION:      "/hr/requisitions",
      HR_REQUISITION:   "/hr/requisitions",
      // Procurement  (router: /procurement/requisitions/:id)
      PROCUREMENT:      "/procurement/requisitions",
      PROCUREMENT_RFQ:  "/procurement/rfqs",
      // Assets  (router: /assets/:id)
      ASSET:            "/assets",
      ASSET_DISPOSAL:   "/assets/disposals",
      // Membership  (router: /hr/membership/members/:memberId)
      MEMBERSHIP:       "/hr/membership/members",
      MEMBERSHIP_APPLICATION: "/hr/membership/members",
      // Airtime
      AIRTIME:          "/hr/airtime/allocations",
    };
    const base = withId[item.referenceType];
    if (base) return `${base}/${item.referenceId}`;
  }

  // Routes where we land on a list/dashboard page (no id needed)
  const listRoutes: Record<string, string> = {
    OFFICIAL_DUTY:  "/hr/leave/official-duty",
    LEAVE_PLAN:     "/hr/leave/plans",
    DOCUMENT:       "/documents",
    ANNOUNCEMENT:   "/notifications",
    NOTIFICATION:   "/notifications",
    // Inventory requests are now created/tracked via Staff Requisition (GOODS type)
    INVENTORY:         "/hr/requisitions/mine",
    INVENTORY_REQUEST: "/hr/requisitions/mine",
  };
  if (item.referenceType && listRoutes[item.referenceType]) {
    return listRoutes[item.referenceType];
  }

  // Use eventType as a last-resort hint
  if (item.eventType) {
    const t = item.eventType.toUpperCase();
    if (t.includes("LEAVE"))       return item.referenceId ? `/hr/leave/requests/${item.referenceId}` : "/hr/leave/requests";
    if (t.includes("CLAIM"))       return item.referenceId ? `/claims/${item.referenceId}` : "/claims";
    if (t.includes("TRAINING"))    return item.referenceId ? `/hr/training/${item.referenceId}` : "/hr/training";
    if (t.includes("INVENTORY"))   return "/hr/requisitions/mine";
    if (t.includes("REQUISITION")) return item.referenceId ? `/hr/requisitions/${item.referenceId}` : "/hr/requisitions";
    if (t.includes("PROCUREMENT")) return item.referenceId ? `/procurement/requisitions/${item.referenceId}` : "/procurement";
    if (t.includes("ASSET"))       return item.referenceId ? `/assets/${item.referenceId}` : "/assets";
    if (t.includes("MEMBERSHIP"))  return item.referenceId ? `/hr/membership/members/${item.referenceId}` : "/hr/membership";
  }

  return null;
}

/** Strip HTML tags from backend notification body for plain-text display */
function stripHtml(html?: string | null): string {
  if (!html) return "";
  return html.replace(/<[^>]*>/g, "").replace(/&amp;/g, "&").replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&nbsp;/g, " ").trim();
}

const PAGE_SIZE = 4;

function NotificationList({
  items,
  onMarkAllRead,
  markingAll,
}: {
  items: DashboardNotificationItem[];
  onMarkAllRead: () => void;
  markingAll: boolean;
}) {
  const [page, setPage] = useState(0);

  const totalPages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
  const safePage   = Math.min(page, totalPages - 1);
  const visible    = items.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);
  const unreadCount = items.filter((i) => i.status === "UNREAD" || i.status === "SENT").length;

  if (!items.length) {
    return (
      <div className="flex items-center gap-3 rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-5">
        <Bell className="h-5 w-5 flex-shrink-0 text-slate-300" />
        <p className="text-sm text-slate-500">No recent notifications.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {/* Unread badge + mark-all */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          {unreadCount > 0 ? (
            <>
              <span className="inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-blue-500 px-1 text-[10px] font-bold text-white">
                {unreadCount}
              </span>
              <span className="text-[11px] font-medium text-slate-500">unread</span>
            </>
          ) : (
            <span className="text-[11px] text-slate-400">All caught up</span>
          )}
        </div>
        <button
          type="button"
          onClick={() => { onMarkAllRead(); setPage(0); }}
          disabled={markingAll || unreadCount === 0}
          className="text-[11px] font-medium text-emerald-700 hover:underline disabled:opacity-40 disabled:cursor-default"
        >
          {markingAll ? "Marking…" : "Mark all read"}
        </button>
      </div>

      {/* Notification rows */}
      <div className="space-y-1.5">
        {visible.map((item) => {
          const unread = item.status === "UNREAD" || item.status === "SENT";
          const route  = notifRoute(item) ?? "/notifications";
          const body   = stripHtml(item.body);
          return (
            <Link
              key={item.id}
              to={route}
              className={`group flex items-start gap-2.5 rounded-xl border px-3 py-2.5 transition hover:shadow-sm ${
                unread
                  ? "border-blue-100 bg-blue-50/60 hover:border-blue-200 hover:bg-blue-50"
                  : "border-slate-100 bg-white hover:border-slate-200"
              }`}
            >
              {/* Read / unread indicator column */}
              <div className="mt-1.5 flex h-4 w-4 shrink-0 items-center justify-center">
                {unread
                  ? <span className="h-2 w-2 rounded-full bg-blue-500" />
                  : <span className="h-2 w-2 rounded-full border-2 border-slate-300" />
                }
              </div>

              <div className="min-w-0 flex-1">
                <p className={`truncate text-xs font-semibold leading-snug ${unread ? "text-slate-900" : "text-slate-500"}`}>
                  {item.title}
                </p>
                {body && (
                  <p className={`truncate text-[11px] leading-snug ${unread ? "text-slate-600" : "text-slate-400"}`}>
                    {body}
                  </p>
                )}
                <p className="mt-0.5 text-[10px] text-slate-400">{fmt(item.createdAt)}</p>
              </div>

              {/* Read label for read items */}
              {!unread && (
                <span className="mt-0.5 shrink-0 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-400">
                  read
                </span>
              )}
              {unread && (
                <ArrowRight className="mt-0.5 h-3.5 w-3.5 shrink-0 text-blue-300 group-hover:text-blue-400" />
              )}
            </Link>
          );
        })}
      </div>

      {/* Pagination footer */}
      <div className="flex items-center justify-between border-t border-slate-100 pt-2">
        <button
          type="button"
          onClick={() => setPage((p) => Math.max(0, p - 1))}
          disabled={safePage === 0}
          className="rounded-lg border border-slate-200 px-2.5 py-1 text-[11px] font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-30 disabled:cursor-default transition"
        >
          ← Prev
        </button>

        <span className="text-[11px] text-slate-400">
          {safePage + 1} / {totalPages}
          {" · "}
          <Link to="/notifications" className="font-semibold text-emerald-700 hover:underline">
            View all
          </Link>
        </span>

        <button
          type="button"
          onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
          disabled={safePage >= totalPages - 1}
          className="rounded-lg border border-slate-200 px-2.5 py-1 text-[11px] font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-30 disabled:cursor-default transition"
        >
          Next →
        </button>
      </div>
    </div>
  );
}

const NOTICE_CATEGORY_TILE: Record<string, { box: string; text: string; label: string }> = {
  MEETING:  { box: "border-blue-100 bg-blue-50",     text: "text-blue-700",   label: "text-blue-600" },
  ACTIVITY: { box: "border-purple-100 bg-purple-50", text: "text-purple-700", label: "text-purple-600" },
  GENERAL:  { box: "border-slate-200 bg-slate-50",   text: "text-slate-700",  label: "text-slate-500" },
};

const NOTICE_CATEGORY_DOT: Record<string, string> = {
  MEETING: "bg-blue-500",
  ACTIVITY: "bg-purple-500",
  GENERAL: "bg-slate-400",
};

const NOTICE_CATEGORY_LABEL: Record<string, string> = {
  MEETING: "Meetings",
  ACTIVITY: "Activities",
  GENERAL: "General",
};

function fmtNoticeDate(iso?: string | null) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString("en-KE", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

/** % of a notice's own start→end window still remaining, for the bar under each row
 *  (a notice with no end date never expires on its own, so it always reads "full"). */
function noticeRemainingPct(notice: Notice): number {
  if (!notice.endAt) return 100;
  const startMs = notice.startAt ? new Date(notice.startAt).getTime() : new Date(notice.createdAt).getTime();
  const endMs = new Date(notice.endAt).getTime();
  const totalMs = endMs - startMs;
  if (!(totalMs > 0)) return 0;
  const remainingMs = endMs - Date.now();
  return Math.max(0, Math.min(100, Math.round((remainingMs / totalMs) * 100)));
}

/** Styled like the Membership card: a header strip of counts, then every notice
 *  listed one per row (title left, when/where right) with a thin bar underneath. */
function NoticeBoard({ notices }: { notices: Notice[] }) {
  const pinMutation = usePinNotice();
  const unpinMutation = useUnpinNotice();
  const remindMutation = useRequestNoticeReminder();
  const unremindMutation = useCancelNoticeReminder();

  function togglePin(notice: Notice) {
    (notice.pinnedByMe ? unpinMutation : pinMutation).mutate(notice.id);
  }

  function toggleReminder(notice: Notice) {
    (notice.reminderRequestedByMe ? unremindMutation : remindMutation).mutate(notice.id);
  }

  if (!notices.length) {
    return (
      <div className="flex items-center gap-3 rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-5">
        <Megaphone className="h-5 w-5 flex-shrink-0 text-slate-300" />
        <p className="text-sm text-slate-500">No notices right now.</p>
      </div>
    );
  }

  const counts: Record<string, number> = { MEETING: 0, ACTIVITY: 0, GENERAL: 0 };
  notices.forEach((notice) => { counts[notice.category] = (counts[notice.category] ?? 0) + 1; });

  return (
    <div>
      {/* Header totals row */}
      <div className="mb-3 grid grid-cols-3 gap-1.5 text-center">
        {(["MEETING", "ACTIVITY", "GENERAL"] as const).map((cat) => (
          <div key={cat} className={`rounded-lg border px-2 py-1.5 ${NOTICE_CATEGORY_TILE[cat].box}`}>
            <p className={`text-base font-bold ${NOTICE_CATEGORY_TILE[cat].text}`}>{counts[cat] ?? 0}</p>
            <p className={`text-[10px] ${NOTICE_CATEGORY_TILE[cat].label}`}>{NOTICE_CATEGORY_LABEL[cat]}</p>
          </div>
        ))}
      </div>

      {/* One row per notice -- line after line, same treatment as the Membership card's bars */}
      <div className="flex max-h-72 flex-col gap-2 overflow-y-auto pr-0.5">
        {notices.map((notice) => {
          const when = fmtNoticeDate(notice.startAt);
          const meta = [when, notice.location].filter(Boolean).join(" \u00b7 ");
          return (
            <div key={notice.id}>
              <div className="mb-0.5 flex items-center justify-between gap-2">
                <span className="flex min-w-0 items-center gap-1.5">
                  {notice.pinned && <Pin className="h-3 w-3 shrink-0 text-amber-500" />}
                  <span className="truncate text-[11px] font-semibold text-slate-700">{notice.title}</span>
                </span>
                <span className="flex shrink-0 items-center gap-1">
                  <span className="text-[11px] font-bold text-slate-800">{meta || "\u2014"}</span>
                  <button
                    type="button"
                    onClick={() => togglePin(notice)}
                    title={notice.pinnedByMe ? "Unpin for me" : "Pin for me"}
                    className={`rounded p-0.5 ${notice.pinnedByMe ? "text-amber-600" : "text-slate-300 hover:text-slate-500"}`}
                  >
                    {notice.pinnedByMe ? <BookmarkCheck className="h-3.5 w-3.5" /> : <Bookmark className="h-3.5 w-3.5" />}
                  </button>
                  <button
                    type="button"
                    onClick={() => toggleReminder(notice)}
                    title={notice.reminderRequestedByMe ? "Cancel reminder" : "Remind me before this starts"}
                    className={`rounded p-0.5 ${notice.reminderRequestedByMe ? "text-emerald-600" : "text-slate-300 hover:text-slate-500"}`}
                  >
                    {notice.reminderRequestedByMe ? <BellRing className="h-3.5 w-3.5" /> : <Bell className="h-3.5 w-3.5" />}
                  </button>
                </span>
              </div>
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                <div
                  className={`h-full rounded-full transition-all ${NOTICE_CATEGORY_DOT[notice.category] ?? NOTICE_CATEGORY_DOT.GENERAL}`}
                  style={{ width: `${noticeRemainingPct(notice)}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Inbox count row ───────────────────────────────────────────────────────────

function InboxRow({
  label,
  count,
  to,
  tone = "slate",
}: {
  label: string;
  count: number;
  to: string;
  tone?: "amber" | "orange" | "teal" | "blue" | "purple" | "slate";
}) {
  const dot: Record<string, string> = {
    amber: "bg-amber-500", orange: "bg-orange-500", teal: "bg-teal-500",
    blue: "bg-blue-500", purple: "bg-purple-500", slate: "bg-slate-400",
  };
  const badge: Record<string, string> = {
    amber: "bg-amber-100 text-amber-700", orange: "bg-orange-100 text-orange-700",
    teal: "bg-teal-100 text-teal-700", blue: "bg-blue-100 text-blue-700",
    purple: "bg-purple-100 text-purple-700", slate: "bg-slate-100 text-slate-600",
  };
  return (
    <Link
      to={to}
      className="flex items-center justify-between rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 hover:border-emerald-200 hover:bg-emerald-50 transition"
    >
      <div className="flex items-center gap-2.5 text-sm font-semibold text-slate-700">
        <span className={`h-2 w-2 rounded-full ${dot[tone]}`} />
        {label}
      </div>
      <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${badge[tone]}`}>
        {count}
      </span>
    </Link>
  );
}

// ── Drill-down modal (used by the top 4 KPI tiles) ────────────────────────────

function DrillDownModal({
  open,
  title,
  description,
  items,
  loading,
  emptyMessage,
  footer,
  onClose,
}: {
  open: boolean;
  title: string;
  description?: string;
  items: DrillDownItem[];
  loading?: boolean;
  emptyMessage: string;
  footer?: React.ReactNode;
  onClose: () => void;
}) {
  const navigate = useNavigate();

  return (
    <Modal open={open} title={title} onClose={onClose}>
      <div className="space-y-3">
        {description && <p className="text-xs text-slate-500">{description}</p>}

        {loading ? (
          <div className="flex items-center justify-center py-10">
            <p className="text-sm text-slate-400">Loading…</p>
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-10 text-center">
            <CheckCircle2 className="h-6 w-6 text-emerald-300" />
            <p className="text-sm text-slate-500">{emptyMessage}</p>
          </div>
        ) : (
          <div className="max-h-96 space-y-1.5 overflow-y-auto pr-1">
            {items.map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={() => { navigate(item.route); onClose(); }}
                className="flex w-full items-center gap-2.5 rounded-xl border border-slate-100 bg-slate-50 px-3 py-2.5 text-left transition hover:border-emerald-200 hover:bg-emerald-50"
              >
                {item.module && <ModuleBadge module={item.module} />}
                <div className="min-w-0 flex-1">
                  <p className="truncate text-xs font-semibold text-slate-800">{item.title}</p>
                  {item.subtitle && <p className="truncate text-xs text-slate-400">{item.subtitle}</p>}
                </div>
                {item.status && <StatusChip value={item.status} />}
                <ArrowRight className="h-3.5 w-3.5 flex-shrink-0 text-slate-400" />
              </button>
            ))}
          </div>
        )}

        {footer}
      </div>
    </Modal>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function DashboardPage() {
  const { hasAnyRole, hasPermission } = useAuth();
  const isAdmin = hasAnyRole(["ADMIN", "SUPER_ADMIN"]);
  const canViewEmployees = isAdmin || hasPermission("HR_EMPLOYEE_VIEW");
  const canManageNotices =
    hasAnyRole(["SUPER_ADMIN"]) || hasPermission("HR_NOTICE_VIEW") || hasPermission("HR_NOTICE_MANAGE");
  const queryClient = useQueryClient();
  const [markAllFlash, setMarkAllFlash] = useState<string | null>(null);
  const [drilldown, setDrilldown] = useState<null | "actions" | "submissions" | "notifications" | "staff">(null);
  const unreadHint = useFeatureHint("DASHBOARD_UNREAD_NOTIFICATIONS", 5);

  const { data, isLoading, error } = useQuery({
    queryKey: ["dashboard"],
    queryFn: fetchDashboard,
    staleTime: 1000 * 60 * 2,
  });

  const { data: memberStats } = useQuery({
    queryKey: ["membership-stats-dashboard"],
    queryFn: membershipApi.getStats,
    staleTime: 1000 * 60 * 5,
  });

  // Shared with the Notice admin page (features/notices/hooks.ts) -- its mutations
  // invalidate this exact query, so a notice created/approved there shows up here
  // right away instead of waiting out a staleTime window or needing a hard refresh.
  const { data: activeNotices } = useActiveNotices();

  // Current financial year: April–March cycle (e.g. "2025/2026")
  const currentFY = (() => {
    const now = new Date();
    const y = now.getFullYear();
    return now.getMonth() >= 3 ? `${y}/${y + 1}` : `${y - 1}/${y}`;
  })();

  const { data: categoryPayments = [] } = useQuery({
    queryKey: ["membership-category-payments-dashboard", currentFY],
    queryFn: () => membershipApi.getCategoryPayments({ financialYear: currentFY }),
    staleTime: 1000 * 60 * 5,
  });

  const { data: whoIsOnLeave } = useQuery({
    queryKey: ["who-is-on-leave-dashboard"],
    queryFn: leaveApi.whoIsOnLeave,
    staleTime: 1000 * 60 * 5,
  });

  const { data: upcomingEvents } = useQuery({
    queryKey: ["calendar-upcoming-dashboard"],
    queryFn: calendarApi.upcoming,
    staleTime: 1000 * 60 * 5,
  });

  const [downloadingIcs, setDownloadingIcs] = useState(false);
  async function downloadCalendar() {
    setDownloadingIcs(true);
    try {
      const blob = await calendarApi.downloadIcs();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "company-calendar.ics";
      a.click();
      window.URL.revokeObjectURL(url);
    } finally {
      setDownloadingIcs(false);
    }
  }

  // Fetched only when the person actually opens the "My pending submissions" tile —
  // pulls each module's own "mine" list and keeps whatever isn't in a finished state.
  const { data: mySubmissions, isFetching: loadingSubmissions } = useQuery({
    queryKey: ["dashboard-my-submissions"],
    queryFn: async (): Promise<DrillDownItem[]> => {
      const [leaveRes, claimRes, trainingRes, reqRes] = await Promise.allSettled([
        leaveApi.myApplications(0, 50),
        fetchMyClaims({ size: 50 }),
        trainingApi.mine({ size: 50 }),
        hrRequisitionApi.mine({ size: 50 }),
      ]);

      const items: DrillDownItem[] = [];

      if (leaveRes.status === "fulfilled") {
        for (const a of leaveRes.value.content) {
          if (TERMINAL_STATUSES.has(a.status)) continue;
          items.push({
            key: `LEAVE-${a.id}`,
            module: "LEAVE",
            title: `${a.leaveTypeName} — ${a.applicationNo}`,
            subtitle: `${a.requestedDays} day${a.requestedDays === 1 ? "" : "s"}`,
            status: a.status,
            route: `/hr/leave/requests/${a.id}`,
          });
        }
      }
      if (claimRes.status === "fulfilled") {
        for (const cl of claimRes.value.content) {
          if (TERMINAL_STATUSES.has(cl.status)) continue;
          items.push({
            key: `CLAIM-${cl.id}`,
            module: "CLAIM",
            title: `${cl.claimType} — ${cl.claimNo}`,
            subtitle: cl.purpose ?? undefined,
            status: cl.status,
            route: `/claims/${cl.id}`,
          });
        }
      }
      if (trainingRes.status === "fulfilled") {
        for (const r of trainingRes.value.content) {
          if (TERMINAL_STATUSES.has(r.status)) continue;
          items.push({
            key: `TRAINING-${r.id}`,
            module: "TRAINING",
            title: r.trainingTitle,
            subtitle: r.trainingRequestNo ?? undefined,
            status: r.status,
            route: `/hr/training/${r.id}`,
          });
        }
      }
      if (reqRes.status === "fulfilled") {
        for (const q of reqRes.value.content) {
          if (TERMINAL_STATUSES.has(q.status)) continue;
          items.push({
            key: `REQUISITION-${q.id}`,
            module: "REQUISITION",
            title: q.purpose || q.serialNo || `Requisition #${q.id}`,
            subtitle: q.serialNo ?? undefined,
            status: q.status,
            route: `/hr/requisitions/${q.id}`,
          });
        }
      }

      return items;
    },
    enabled: drilldown === "submissions",
    staleTime: 1000 * 30,
  });

  // Staff Accounts tile + drill-down: every user account that belongs to an actual
  // employee -- system/administrator accounts (SUPER_ADMIN, ADMIN) are left off this
  // list on purpose, since it's meant to read as "our people," not "every login."
  // Always on (like the other KPI tiles) so the tile's own count is live, not just the
  // modal contents once opened.
  const { data: staffAccountsList, isFetching: loadingStaff } = useQuery({
    queryKey: ["dashboard-staff-accounts"],
    queryFn: async (): Promise<DrillDownItem[]> => {
      const page = await fetchUsers({ size: 500 });
      return page.content
        .filter((u) => {
          const roleCodes = (u.roleCodes?.length ? u.roleCodes : (u.roles ?? [])).map((r) => r.toUpperCase());
          return !roleCodes.includes("SUPER_ADMIN") && !roleCodes.includes("ADMIN");
        })
        .map((u) => ({
          key: `USER-${u.id}`,
          title: u.employee?.fullName || u.fullName || u.username,
          subtitle: [u.employee?.jobTitle, u.employee?.departmentName].filter(Boolean).join(" \u00b7 ") || u.email,
          route: `/admin/users/${u.id}`,
        }));
    },
    enabled: canViewEmployees,
    staleTime: 1000 * 30,
  });

  const { mutate: markAll, isPending: markingAll } = useMutation({
    mutationFn: markAllNotificationsRead,
    onSuccess: ({ marked }) => {
      setMarkAllFlash(`${marked} notification${marked === 1 ? "" : "s"} marked as read.`);
      setTimeout(() => setMarkAllFlash(null), 3000);
      void queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });

  if (isLoading) return <PageLoader label="Loading dashboard…" />;
  if (error || !data) {
    return <ErrorState message="Failed to load dashboard. Please refresh and try again." />;
  }

  const s = data.stats;

  // Finance/MD approval stages were dropped from Staff Requisitions (2026-07-09) — Service/
  // Works/General requisitions are no longer created here, so those counts (if nonzero, from
  // historical records) intentionally aren't rolled into the actionable inbox total anymore;
  // there's no page left to act on them from.
  const myInboxTotal =
    n(s.pendingLeaveApprovals) + n(s.pendingClaimApprovals) +
    n(s.pendingTrainingApprovals) + n(s.pendingInventoryApprovals) +
    n(s.pendingRequisitionApprovals) + n(s.pendingProcurementActions);

  const myPendingTotal =
    n(s.myPendingLeaves) + n(s.myPendingClaims) +
    n(s.myPendingTraining) + n(s.myPendingInventoryRequests) +
    n(s.myPendingRequisitions);

  const pendingApprovals = data.pendingApprovals ?? [];
  const notifications = data.notifications ?? [];
  const quickActions = data.quickActions ?? [];

  const activeUsers = Math.max(n(s.enabledUsers) - n(s.lockedUsers), 0);
  const inactiveUsers = Math.max(n(s.totalUsers) - n(s.enabledUsers), 0);
  const userStatusData = [
    { name: "Active", value: activeUsers },
    { name: "Locked", value: n(s.lockedUsers) },
    { name: "Inactive", value: inactiveUsers },
  ];

  const pieColors = ["#10b981", "#f59e0b", "#94a3b8"];

  return (
    <div className="space-y-8">

      {/* ── Top KPIs ─────────────────────────────────────────────────────── */}
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label="Items awaiting my action"
          value={myInboxTotal}
          icon={ClipboardCheck}
          tone={myInboxTotal > 0 ? "amber" : "slate"}
          onClick={() => setDrilldown("actions")}
        />
        <StatCard
          label="My pending submissions"
          value={myPendingTotal}
          icon={WalletCards}
          tone={myPendingTotal > 0 ? "blue" : "slate"}
          onClick={() => setDrilldown("submissions")}
        />
        {unreadHint.shouldShow && (
          <StatCard
            label="Unread notifications"
            value={n(s.unreadNotifications)}
            icon={Bell}
            tone={n(s.unreadNotifications) > 0 ? "blue" : "slate"}
            onClick={() => setDrilldown("notifications")}
          />
        )}
        {canViewEmployees ? (
          <StatCard
            label="Staff Accounts"
            value={staffAccountsList?.length ?? 0}
            icon={Users}
            tone="emerald"
            onClick={() => setDrilldown("staff")}
          />
        ) : (
          <StatCard
            label="My requisitions pending"
            value={n(s.myPendingRequisitions)}
            icon={ShoppingCart}
            tone="orange"
            to="/hr/requisitions/mine"
          />
        )}
        <StatCard
          label="Upcoming events"
          value={upcomingEvents?.length ?? 0}
          icon={CalendarDays}
          tone={upcomingEvents && upcomingEvents.length > 0 ? "teal" : "slate"}
          onClick={() => document.getElementById("upcoming-events")?.scrollIntoView({ behavior: "smooth", block: "start" })}
        />
      </div>

      {/* ── Notice Board (everyone -- meetings, ongoing activities, announcements).
          Built once and reused below: full-width on its own for most employees,
          or as the third card alongside Leave Status/Membership for anyone who
          can also see those (restores the original three-card row). ── */}
      {(() => {
        const notices = activeNotices ?? [];
        const noticeBoardCard = (
          <SectionCard
            title="Notice Board"
            description={`${notices.length} on the board${notices.filter((nt) => nt.pinned).length ? ` \u00b7 ${notices.filter((nt) => nt.pinned).length} pinned` : ""}`}
            action={
              canManageNotices ? (
                <Link to="/admin/notices" className="text-xs font-semibold text-emerald-700 hover:underline">
                  Manage
                </Link>
              ) : undefined
            }
          >
            <NoticeBoard notices={notices} />
          </SectionCard>
        );

        if (!canViewEmployees) {
          return noticeBoardCard;
        }

        return (
          <div className="grid gap-6 xl:grid-cols-3">

            {/* Leave Status */}
          <SectionCard title="Leave Status">
            <div className="flex flex-col gap-2">
              <Link
                to="/hr/leave/on-leave"
                className="flex items-center gap-3 rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-3 hover:shadow-sm transition"
              >
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-emerald-100">
                  <CalendarDays className="h-4 w-4 text-emerald-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] font-medium text-emerald-600">On Leave</p>
                  <p className="text-xs text-slate-500">Currently on approved leave</p>
                </div>
                <p className="text-xl font-bold text-emerald-700">
                  {whoIsOnLeave?.currentlyOnLeave?.length ?? 0}
                </p>
              </Link>

              <Link
                to="/hr/leave/on-leave"
                className="flex items-center gap-3 rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 hover:shadow-sm transition"
              >
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-100">
                  <BriefcaseBusiness className="h-4 w-4 text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] font-medium text-blue-600">On Official Duty</p>
                  <p className="text-xs text-slate-500">Currently on official assignment</p>
                </div>
                <p className="text-xl font-bold text-blue-700">
                  {whoIsOnLeave?.onOfficialDuty?.length ?? 0}
                </p>
              </Link>

              <Link
                to="/hr/leave/on-leave"
                className="flex items-center gap-3 rounded-xl border border-amber-100 bg-amber-50 px-4 py-3 hover:shadow-sm transition"
              >
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-amber-100">
                  <CalendarDays className="h-4 w-4 text-amber-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] font-medium text-amber-600">Upcoming Leave</p>
                  <p className="text-xs text-slate-500">Approved, starting soon</p>
                </div>
                <p className="text-xl font-bold text-amber-700">
                  {whoIsOnLeave?.upcomingLeave?.length ?? 0}
                </p>
              </Link>
            </div>
          </SectionCard>

          {/* Membership breakdown */}
          <SectionCard
            title="Membership"
            description={`${n(memberStats?.totalMembers)} total · ${currentFY}`}
          >
            {/* Header totals row */}
            <div className="mb-3 grid grid-cols-3 gap-1.5 text-center">
              <Link to="/membership" className="rounded-lg border border-emerald-100 bg-emerald-50 px-2 py-1.5 hover:shadow-sm transition">
                <p className="text-base font-bold text-emerald-700">{n(memberStats?.activeMembers)}</p>
                <p className="text-[10px] text-emerald-600">Active</p>
              </Link>
              <Link to="/membership?status=SUSPENDED" className="rounded-lg border border-amber-100 bg-amber-50 px-2 py-1.5 hover:shadow-sm transition">
                <p className="text-base font-bold text-amber-700">{n(memberStats?.suspendedMembers)}</p>
                <p className="text-[10px] text-amber-600">Suspended</p>
              </Link>
              <Link to="/membership/applications" className="rounded-lg border border-blue-100 bg-blue-50 px-2 py-1.5 hover:shadow-sm transition">
                <p className="text-base font-bold text-blue-700">{n(memberStats?.totalApplications)}</p>
                <p className="text-[10px] text-blue-600">Applications</p>
              </Link>
            </div>

            {/* Per-category bars
                Primary:  memberStats.categoryBreakdown (stats endpoint — authoritative member counts)
                Enriched: joined with categoryPayments (payment health for current FY)
                Fallback: categoryPayments alone if backend not yet updated */}
            {(() => {
              // Build payment lookup keyed by categoryId
              const payMap = new Map(categoryPayments.map((p) => [p.categoryId, p]));

              // Prefer categoryBreakdown from stats (correct active counts, no FY dependency)
              const rows = (memberStats?.categoryBreakdown ?? []).length > 0
                ? (memberStats!.categoryBreakdown!).map((cat) => ({
                    categoryId:  cat.categoryId,
                    categoryName: cat.categoryName,
                    memberCount:  cat.memberCount,
                    paidCount:    payMap.get(cat.categoryId)?.paidCount    ?? null,
                    overdueCount: payMap.get(cat.categoryId)?.overdueCount ?? null,
                    unpaidCount:  payMap.get(cat.categoryId)?.unpaidCount  ?? null,
                  }))
                // Fallback: use category-payments data (category counts may under-report if no fee record)
                : categoryPayments.map((cat) => ({
                    categoryId:   cat.categoryId,
                    categoryName: cat.categoryName,
                    memberCount:  cat.memberCount,
                    paidCount:    cat.paidCount,
                    overdueCount: cat.overdueCount,
                    unpaidCount:  cat.unpaidCount,
                  }));

              if (rows.length === 0) {
                return (
                  <div className="flex h-20 items-center justify-center">
                    <p className="text-xs text-slate-400">No category data available</p>
                  </div>
                );
              }

              const grandTotal = rows.reduce((s, r) => s + r.memberCount, 0) || 1;

              return (
                <div className="flex flex-col gap-2">
                  {rows.map((cat) => {
                    const barPct     = Math.round((cat.memberCount / grandTotal) * 100);
                    const paidPct    = cat.paidCount != null && cat.memberCount > 0
                      ? Math.round((cat.paidCount / cat.memberCount) * 100)
                      : null;
                    return (
                      <div key={cat.categoryId}>
                        <div className="mb-0.5 flex items-center justify-between">
                          <span className="truncate text-[11px] font-semibold text-slate-700">{cat.categoryName}</span>
                          <div className="ml-2 flex shrink-0 items-center gap-1.5">
                            {(cat.overdueCount ?? 0) > 0 && (
                              <span className="rounded bg-red-100 px-1 text-[10px] font-semibold text-red-600">
                                {cat.overdueCount} overdue
                              </span>
                            )}
                            <span className="text-[11px] font-bold text-slate-800">{cat.memberCount}</span>
                          </div>
                        </div>
                        <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                          <div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${barPct}%` }} />
                        </div>
                        {paidPct !== null && (
                          <p className="mt-0.5 text-[10px] text-slate-400">
                            {paidPct}% paid{cat.unpaidCount ? ` · ${cat.unpaidCount} outstanding` : ""}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </SectionCard>

            {noticeBoardCard}
          </div>
        );
      })()}

      {/* ── Approval inboxes ──────────────────────────────────────────────── */}
      {myInboxTotal > 0 && (
        <SectionCard title="My approval inboxes">
          <div className="max-h-52 overflow-y-auto pr-0.5">
            <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
              {n(s.pendingLeaveApprovals) > 0 && (
                <InboxRow label="Leave approvals" count={s.pendingLeaveApprovals} to="/hr/leave/approvals" tone="blue" />
              )}
              {n(s.pendingClaimApprovals) > 0 && (
                <InboxRow label="Claim approvals" count={s.pendingClaimApprovals} to="/claims/approvals" tone="amber" />
              )}
              {n(s.pendingTrainingApprovals) > 0 && (
                <InboxRow label="Training approvals" count={s.pendingTrainingApprovals} to="/hr/training/inbox" tone="purple" />
              )}
              {n(s.pendingRequisitionApprovals) > 0 && (
                <InboxRow label="Requisition approvals" count={s.pendingRequisitionApprovals} to="/hr/requisitions/inbox" tone="orange" />
              )}
              {n(s.pendingProcurementActions) > 0 && (
                <InboxRow label="Procurement actions" count={s.pendingProcurementActions} to="/hr/requisitions/inbox" tone="orange" />
              )}
            </div>
          </div>
        </SectionCard>
      )}

      {/* ── Upcoming Events (company calendar) ──────────────────────────── */}
      <SectionCard
        id="upcoming-events"
        title="Upcoming Events"
        action={
          <button
            type="button"
            onClick={downloadCalendar}
            disabled={downloadingIcs}
            className="flex items-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-100 disabled:opacity-60"
          >
            <Download className="h-3.5 w-3.5" />
            {downloadingIcs ? "Preparing…" : "Download Calendar (.ics)"}
          </button>
        }
      >
        {upcomingEvents && upcomingEvents.length > 0 ? (
          <div className="grid gap-2">
            {upcomingEvents.slice(0, 5).map((ev) => (
              <div
                key={ev.id}
                className="flex items-center gap-3 rounded-xl border border-slate-100 bg-slate-50 px-4 py-2.5"
              >
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-emerald-100 text-emerald-700">
                  <CalendarDays className="h-4.5 w-4.5" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-slate-800">{ev.title}</p>
                  <p className="truncate text-xs text-slate-500">
                    {fmt(ev.startAt)}{ev.location ? ` · ${ev.location}` : ""}
                  </p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-slate-400">No upcoming events scheduled.</p>
        )}
      </SectionCard>

      {/* ── Priority actions · Notifications · Quick access (always last) ── */}
      <div className="grid gap-6 xl:grid-cols-3">
        <SectionCard title="Priority actions">
          <ApprovalList items={pendingApprovals} />
        </SectionCard>

        <SectionCard
          title="Notifications"
          action={
            <Link to="/notifications" className="text-xs font-semibold text-emerald-700 hover:underline">
              View all
            </Link>
          }
        >
          {markAllFlash && (
            <div className="mb-3 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-700">
              {markAllFlash}
            </div>
          )}
          <NotificationList
            items={notifications}
            onMarkAllRead={() => markAll()}
            markingAll={markingAll}
          />
        </SectionCard>

        <SectionCard title="Quick access">
          <div className="grid gap-1.5">
            {quickActions.length ? (
              <>
                {quickActions.slice(0, 3).map((a) => (
                  <Link
                    key={a.route}
                    to={a.route}
                    className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:border-emerald-200 hover:bg-emerald-50 hover:text-emerald-700 transition"
                  >
                    <span>{a.title}</span>
                    <ArrowRight className="h-3.5 w-3.5 text-slate-400" />
                  </Link>
                ))}
                {quickActions.length > 3 && (
                  <div className="grid grid-cols-2 gap-1.5 pt-0.5">
                    {quickActions.slice(3, 7).map((a) => (
                      <Link
                        key={a.route}
                        to={a.route}
                        className="truncate rounded-xl border border-slate-100 bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-500 hover:border-emerald-200 hover:bg-emerald-50 hover:text-emerald-700 transition"
                      >
                        {a.title}
                      </Link>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <p className="text-sm text-slate-400">No quick actions available.</p>
            )}
          </div>
        </SectionCard>
      </div>

      <DrillDownModal
        open={drilldown === "actions"}
        title="Items awaiting my action"
        items={pendingApprovals.map((a) => ({
          key: `${a.module ?? a.type ?? "ITEM"}-${a.id}`,
          module: a.module ?? a.type ?? undefined,
          title: a.referenceNo ?? a.title ?? `#${a.id}`,
          subtitle: a.submittedBy ?? a.description ?? undefined,
          status: a.status,
          route: a.route ?? "/approvals",
        }))}
        emptyMessage="All caught up — nothing needs your action right now."
        footer={
          <Link
            to="/approvals"
            onClick={() => setDrilldown(null)}
            className="block pt-1 text-center text-xs font-semibold text-emerald-700 hover:underline"
          >
            View all approvals →
          </Link>
        }
        onClose={() => setDrilldown(null)}
      />

      <DrillDownModal
        open={drilldown === "submissions"}
        title="My pending submissions"
        items={mySubmissions ?? []}
        loading={loadingSubmissions}
        emptyMessage="Nothing of yours is waiting on approval right now."
        onClose={() => setDrilldown(null)}
      />

      <DrillDownModal
        open={drilldown === "notifications"}
        title="Unread notifications"
        items={notifications
          .filter((it) => it.status === "UNREAD" || it.status === "SENT")
          .map((it) => ({
            key: `NOTIF-${it.id}`,
            title: it.title,
            subtitle: stripHtml(it.body) || undefined,
            route: notifRoute(it) ?? "/notifications",
          }))}
        emptyMessage="You're all caught up."
        footer={
          <Link
            to="/notifications"
            onClick={() => setDrilldown(null)}
            className="block pt-1 text-center text-xs font-semibold text-emerald-700 hover:underline"
          >
            View all notifications →
          </Link>
        }
        onClose={() => setDrilldown(null)}
      />

      <DrillDownModal
        open={drilldown === "staff"}
        title="Staff Accounts"
        description={`${staffAccountsList?.length ?? 0} staff account${(staffAccountsList?.length ?? 0) === 1 ? "" : "s"}`}
        items={staffAccountsList ?? []}
        loading={loadingStaff}
        emptyMessage="No staff accounts found."
        footer={
          <Link
            to="/admin/users"
            onClick={() => setDrilldown(null)}
            className="block pt-1 text-center text-xs font-semibold text-emerald-700 hover:underline"
          >
            Manage all users →
          </Link>
        }
        onClose={() => setDrilldown(null)}
      />
    </div>
  );
}
